import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, Loader } from 'lucide-react';
import TaskCard from '../tasks/TaskCard';
import TaskDetailsPanel from '../tasks/TaskDetailsPanel';
import Pagination from '../shared/Pagination';
import { useToast, ToastContainer } from '../shared/Toast';
import { getTasks } from '../../services/perfex-api';

/**
 * Tasks Tab Component
 * Displays tasks from Perfex CRM with inline editing and completion
 */
const TasksTab = () => {
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalTasks, setTotalTasks] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dateFilter, setDateFilter] = useState('today'); // today, tomorrow, week, nextweek
    const { toasts, showToast, removeToast } = useToast();

    const itemsPerPage = 20;

    // Load tasks on mount and page change
    useEffect(() => {
        loadTasks();
    }, [currentPage]);

    // Filter tasks based on date filter
    useEffect(() => {
        if (tasks.length === 0) {
            setFilteredTasks([]);
            return;
        }

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);

        // Calculate next week (starts on Sunday)
        const nextWeekStart = new Date(today);
        const daysUntilSunday = 7 - today.getDay();
        nextWeekStart.setDate(today.getDate() + daysUntilSunday);
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);

        const filtered = tasks.filter(task => {
            const startDateField = task.startdate || task.start_date;
            if (!startDateField) return false;

            // Parse date string (YYYY-MM-DD format) to avoid timezone issues
            let taskDate;
            if (typeof startDateField === 'string' && startDateField.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = startDateField.split('-').map(Number);
                taskDate = new Date(year, month - 1, day);
            } else {
                taskDate = new Date(startDateField);
            }

            const taskDay = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());

            switch (dateFilter) {
                case 'today':
                    return taskDay.getTime() === today.getTime();
                case 'tomorrow':
                    return taskDay.getTime() === tomorrow.getTime();
                case 'week':
                    return taskDay >= today && taskDay <= weekEnd;
                case 'nextweek':
                    return taskDay >= nextWeekStart && taskDay <= nextWeekEnd;
                default:
                    return true;
            }
        });

        setFilteredTasks(filtered);
    }, [tasks, dateFilter]);

    const loadTasks = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await getTasks(currentPage, itemsPerPage);

            if (result.success) {
                // Handle different API response structures
                const taskData = result.data?.tasks || result.data || [];
                const total = result.data?.total || taskData.length;

                setTasks(Array.isArray(taskData) ? taskData : []);
                setTotalTasks(total);

                // If current page is empty and we're not on page 1, go back a page
                if (taskData.length === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                }
            } else {
                throw new Error(result.data?.message || 'Failed to load tasks');
            }
        } catch (err) {
            console.error('Error loading tasks:', err);
            setError(err.message || 'Failed to load tasks from Perfex CRM');
            showToast('Failed to load tasks: ' + err.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTaskSelect = (task) => {
        setSelectedTask(task);
    };

    const handleTaskUpdate = (updatedTask) => {
        // Update task in list
        setTasks((prevTasks) =>
            prevTasks.map((task) => (task.id === updatedTask.id ? updatedTask : task))
        );

        // Update selected task
        setSelectedTask(updatedTask);

        showToast('Task updated successfully', 'success');
    };

    const handleTaskComplete = (taskId) => {
        // Remove completed task from list
        setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));

        // Clear selection if completed task was selected
        if (selectedTask?.id === taskId) {
            setSelectedTask(null);
        }

        showToast('Task marked as complete', 'success');

        // Reload tasks to get fresh data
        setTimeout(() => loadTasks(), 1000);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setSelectedTask(null); // Clear selection when changing pages
    };

    const handleRefresh = () => {
        setSelectedTask(null);
        loadTasks();
    };

    return (
        <div className="flex h-full bg-gray-50">
            {/* Left Panel - Task List */}
            <div className="w-full md:w-2/5 lg:w-1/3 border-r border-gray-200 flex flex-col bg-white">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-blue-600 text-white">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-xl font-bold">Tasks</h2>
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                            title="Refresh tasks"
                        >
                            <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                    <p className="text-sm text-green-100 mb-3">
                        {filteredTasks.length} of {totalTasks} task{totalTasks !== 1 ? 's' : ''}
                    </p>

                    {/* Date Filter Tabs */}
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => setDateFilter('today')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'today'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            Today
                        </button>
                        <button
                            onClick={() => setDateFilter('tomorrow')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'tomorrow'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            Tomorrow
                        </button>
                        <button
                            onClick={() => setDateFilter('week')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'week'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            This Week
                        </button>
                        <button
                            onClick={() => setDateFilter('nextweek')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'nextweek'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            Next Week
                        </button>
                    </div>
                </div>

                {/* Task List */}
                <div className="flex-1 overflow-y-auto">
                    {isLoading && tasks.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500">
                                <Loader size={48} className="mx-auto mb-3 animate-spin text-green-600" />
                                <p>Loading tasks...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                                    <div>
                                        <h3 className="font-semibold text-red-800 mb-1">
                                            Error Loading Tasks
                                        </h3>
                                        <p className="text-sm text-red-700">{error}</p>
                                        <button
                                            onClick={handleRefresh}
                                            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : filteredTasks.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500 p-6">
                                <AlertCircle size={48} className="mx-auto mb-3 opacity-30" />
                                <p className="text-lg font-semibold mb-2">No Tasks Found</p>
                                <p className="text-sm">
                                    {tasks.length === 0
                                        ? 'There are no tasks to display for the current page.'
                                        : `No tasks found for ${
                                            dateFilter === 'today' ? 'today' :
                                            dateFilter === 'tomorrow' ? 'tomorrow' :
                                            dateFilter === 'nextweek' ? 'next week' : 'this week'
                                        }.`
                                    }
                                </p>
                                {currentPage > 1 && tasks.length === 0 && (
                                    <button
                                        onClick={() => setCurrentPage(1)}
                                        className="mt-4 text-green-600 hover:text-green-700 font-semibold"
                                    >
                                        Go to First Page
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            {filteredTasks.map((task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    isSelected={selectedTask?.id === task.id}
                                    onClick={() => handleTaskSelect(task)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!isLoading && !error && tasks.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalItems={totalTasks}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>

            {/* Right Panel - Task Details */}
            <div className="hidden md:block flex-1">
                <TaskDetailsPanel
                    task={selectedTask}
                    onUpdate={handleTaskUpdate}
                    onComplete={handleTaskComplete}
                />
            </div>

            {/* Mobile: Task details as overlay */}
            {selectedTask && (
                <div className="md:hidden fixed inset-0 bg-white z-50 flex flex-col">
                    <div className="p-4 border-b border-gray-200 bg-green-600 text-white flex items-center justify-between">
                        <h2 className="text-lg font-bold">Task Details</h2>
                        <button
                            onClick={() => setSelectedTask(null)}
                            className="px-4 py-2 bg-white/20 rounded-lg font-semibold hover:bg-white/30 transition-colors"
                        >
                            Back
                        </button>
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <TaskDetailsPanel
                            task={selectedTask}
                            onUpdate={handleTaskUpdate}
                            onComplete={handleTaskComplete}
                        />
                    </div>
                </div>
            )}

            {/* Toast Notifications */}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
    );
};

export default TasksTab;
